import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Switch,
  IconButton,
  Fab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import StoreIcon from '@mui/icons-material/Store';
import AppHeader from '../components/AppHeader';
import AlertModal from '../components/AlertModal';
import { useBranch } from '../contexts/BranchContext';
import { getFoodItems, updateFoodItem } from '../services/menuService';

const FoodItemCard = ({ item, onToggle, onEdit, isUpdating }) => (
  <Card sx={{ mb: 1.5, borderRadius: 3, overflow: 'visible' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#059669', mb: 1 }}>
            {item.hasVariants ? 'Multiple variants' : `₹${item.price}`}
          </Typography>
          {item.description && (
            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 1 }}>
              {item.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#6B7280', bgcolor: '#F3F4F6', px: 1, py: 0.25, borderRadius: 1 }}>
              {item.category?.name || 'Uncategorized'}
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#6B7280', bgcolor: '#F3F4F6', px: 1, py: 0.25, borderRadius: 1 }}>
              {item.dishType === 'veg' ? 'Veg' : 'Non-Veg'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Switch
            checked={item.isAvailable}
            onChange={() => onToggle(item)}
            disabled={isUpdating}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
            }}
          />
          <IconButton
            size="small"
            onClick={() => onEdit(item)}
            sx={{ bgcolor: '#F3F4F6', '&:hover': { bgcolor: '#E5E7EB' } }}
          >
            <EditIcon sx={{ fontSize: 18, color: '#851E41' }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.isAvailable ? '#10B981' : '#EF4444' }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: item.isAvailable ? '#10B981' : '#EF4444' }}>
          {item.isAvailable ? 'Available' : 'Unavailable'}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const MenuPage = () => {
  const navigate = useNavigate();
  const { selectedBranch, listenAllMode, setBranchSelectModalVisible } = useBranch();
  const [foodItems, setFoodItems] = useState({});
  const [loading, setLoading] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  const fetchFoodItems = async () => {
    if (!selectedBranch?._id) return;
    try {
      setLoading(true);
      const response = await getFoodItems(selectedBranch._id);
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        setFoodItems(response);
      } else if (Array.isArray(response)) {
        const grouped = response.reduce((acc, item) => {
          const cat = item.category?.name || 'Uncategorized';
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(item);
          return acc;
        }, {});
        setFoodItems(grouped);
      } else {
        setFoodItems({});
      }
    } catch {
      showAlert('Error', 'Failed to load menu items');
      setFoodItems({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch?._id) {
      fetchFoodItems();
    } else {
      setFoodItems({});
    }
  }, [selectedBranch?._id]);

  const toggleItemAvailability = async (foodItem) => {
    if (!selectedBranch?._id) return;
    try {
      setUpdatingItems(prev => new Set(prev).add(foodItem._id));
      await updateFoodItem(foodItem._id, { isAvailable: !foodItem.isAvailable }, selectedBranch._id);
      setFoodItems(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(cat => {
          next[cat] = next[cat].map(item =>
            item._id === foodItem._id ? { ...item, isAvailable: !item.isAvailable } : item
          );
        });
        return next;
      });
    } catch {
      showAlert('Error', 'Failed to update item availability');
    } finally {
      setUpdatingItems(prev => {
        const s = new Set(prev);
        s.delete(foodItem._id);
        return s;
      });
    }
  };

  const handleEditItem = (foodItem) => {
    navigate('/food-detail', { state: { foodItem, branchId: selectedBranch._id } });
  };

  const handleAddFoodItem = () => {
    if (!selectedBranch?._id) {
      showAlert('Error', 'Please select a branch first.');
      return;
    }
    navigate('/food-detail', { state: { branchId: selectedBranch._id } });
  };

  const categories = Object.keys(foodItems);

  // listen-all mode: no branch picked yet
  if (listenAllMode && !selectedBranch) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader title="Menu" />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, px: 3, textAlign: 'center' }}>
          <StoreIcon sx={{ fontSize: 56, color: '#D1D5DB', mb: 2 }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#374151', mb: 1 }}>
            Select a Branch
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 3 }}>
            Pick a branch to view and manage its menu items.
          </Typography>
          <Button
            variant="contained"
            startIcon={<StoreIcon />}
            onClick={() => setBranchSelectModalVisible(true)}
            sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' }, px: 3, py: 1.5 }}
          >
            Select Branch
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader title="Menu" />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress sx={{ color: '#851E41' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pb: 8 }}>
      <AppHeader title="Menu" />
      <AlertModal
        visible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        onRequestClose={() => setAlertVisible(false)}
      />

      {categories.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
          <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <RestaurantMenuIcon sx={{ fontSize: 48, color: '#D1D5DB' }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#374151', mb: 1 }}>No Menu Items</Typography>
          <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 2 }}>Start by adding your first food item.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFoodItem}
            sx={{ bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' }, px: 3, py: 1.5 }}>
            Add food item
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          {categories.map(cat => (
            <Box key={cat} sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937', mb: 2 }}>{cat}</Typography>
              {foodItems[cat].map(item => (
                <FoodItemCard
                  key={item._id}
                  item={item}
                  onToggle={toggleItemAvailability}
                  onEdit={handleEditItem}
                  isUpdating={updatingItems.has(item._id)}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}

      {selectedBranch?._id && categories.length > 0 && (
        <Fab color="primary" onClick={handleAddFoodItem}
          sx={{ position: 'fixed', bottom: 24, right: 24, bgcolor: '#851E41', '&:hover': { bgcolor: '#6B1830' } }}>
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MenuPage;
